import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CollapsibleComponent from "./collapsibleComponents";
import { CustomerDetailResponse, CustomerDetailValue } from "../../interfaces/customerDetail";
import { ReferenceContactsSection } from "./referenceContactSection";

interface AdditionalContactReferences {
    client_reference_id: string;
    customerDetailData: CustomerDetailResponse[];
}

type CustomerDetailNestedMap = Map<
    string, // customer_detail_field_id
    Map<
        string, // group_id
        CustomerDetailResponse[] // array of customer_detail_response which contains  ( customer_detail, field, customer_detail_field, customer_detail_group_ref )
    >
>;

const styles = {
    itemContainer: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '16px',
    }
};

const requiredFieldNames = ['email', 'contact_number'];

const AdditionalContactReferences: React.FC<AdditionalContactReferences> = ({ client_reference_id, customerDetailData }) => {
    const [currentLoanIdCustomerDetail, setCurrentLoanIdCustomerDetail] = useState<CustomerDetailNestedMap>(new Map());
    const [otherLoanIdCustomerDetailMap, setOtherLoanIdCustomerDetailMap] = useState<CustomerDetailNestedMap>(new Map());
    const [customerDetails, setCustomerDetails] = useState<CustomerDetailValue[]>([]);

    useEffect(() => {
        if (customerDetailData?.length) {
            const currentLoanIdCustomerDetailMap: CustomerDetailNestedMap = new Map();
            const otherLoanIdCustomerDetailMap: CustomerDetailNestedMap = new Map();
            const customerDetailSet = new Set<string>();
            const groupIdMap = new Map();
            let existingCustomerDetailIds = new Set<string>();
            for (const item of customerDetailData) {
                const clientRefId = item.customer_detail_group_ref?.client_reference_id;
                const detailFieldId = item.customer_detail_field.field_id;
                const groupId = item.customer_detail.group_id;

                if (
                    item.customer_detail.field_value &&
                    item.customer_detail_field.field_name === 'customer' &&
                    requiredFieldNames.includes(item.field.field_name) &&
                    item.customer_detail.score > 0
                ) {
                    customerDetailSet.add(JSON.stringify({
                        field_name: item.field.field_name,
                        field_value: item.customer_detail.field_value,
                        score: item.customer_detail.score,
                        updated_timestamp: new Date(item.customer_detail.updated_date).getTime()
                    }));
                }

                if (!clientRefId || !detailFieldId || !groupId) continue;
                if (clientRefId === client_reference_id) {
                    if (!currentLoanIdCustomerDetailMap.has(detailFieldId)) {
                        currentLoanIdCustomerDetailMap.set(detailFieldId, new Map())
                    }
                    const groupMap = currentLoanIdCustomerDetailMap.get(detailFieldId)!;

                    if (!groupMap.has(groupId)) {
                        groupMap.set(groupId, []);
                    }
                    groupMap.get(groupId)!.push(item);

                } else {
                    if (!existingCustomerDetailIds.has(item.customer_detail.customer_detail_id)) {

                        if (!otherLoanIdCustomerDetailMap.has(detailFieldId)) {
                            otherLoanIdCustomerDetailMap.set(detailFieldId, new Map());
                        }
                        const groupMap = otherLoanIdCustomerDetailMap.get(detailFieldId)!;

                        if (!groupMap.has(groupId)) {
                            groupMap.set(groupId, []);
                        }
                        const detailArray = groupMap.get(groupId)!;

                        // Ensure deduplication by customer_detail_id
                        if (!existingCustomerDetailIds.has(item.customer_detail.customer_detail_id)) {
                            detailArray.push(item);
                            existingCustomerDetailIds.add(item.customer_detail.customer_detail_id);
                        }
                    }
                }
                if (!groupIdMap.has(groupId)) {
                    groupIdMap.set(groupId, []);
                }
                if (!groupIdMap.get(groupId).includes(clientRefId)) {
                    groupIdMap.get(groupId).push(clientRefId);
                }
            }
            setCurrentLoanIdCustomerDetail(currentLoanIdCustomerDetailMap);
            setOtherLoanIdCustomerDetailMap(otherLoanIdCustomerDetailMap);
            setCustomerDetails([
                ...Array.from(customerDetailSet).map(item => JSON.parse(item) as CustomerDetailValue).sort((a, b) => {
                    if (a.score === b.score) {
                        return b.updated_timestamp - a.updated_timestamp;
                    }
                    return b.score - a.score;
                }),
            ]);
        }
    }, [customerDetailData]);

    return (
        <>
            {customerDetails.length !== 0 && (
                <CollapsibleComponent
                    headerText="Customer Details"
                    headerStyle={{ backgroundColor: '#FFEBE5' }}
                    content={
                        <Box>
                            {customerDetails.map((item: CustomerDetailValue, index: number) => {
                                return (
                                    <Box key={index}>
                                        <Box sx={styles.itemContainer}>
                                            {item.field_name === 'contact_number' ? (
                                                <PhoneIcon fontSize="small" />
                                            ) : item.field_name === 'email' ? (
                                                <AlternateEmailIcon fontSize="small" />
                                            ) : null}
                                            <Typography variant="body2">{item.field_value}</Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    }
                />
            )}

            {currentLoanIdCustomerDetail && (
                <ReferenceContactsSection
                    title="Reference Contacts from current Loan ID"
                    detailMap={currentLoanIdCustomerDetail}
                />
            )}

            {otherLoanIdCustomerDetailMap && (
                <ReferenceContactsSection
                    title="Reference Contacts from other Loan IDs"
                    detailMap={otherLoanIdCustomerDetailMap}
                />
            )}
        </>
    );
};

export default AdditionalContactReferences;
